import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TASKALL, WORKTASK, TASK } from "../../services/actions";
import { Box, Container, Grid, Pagination, Typography, Stack, Divider, Chip } from "@mui/material";
import { BsCheckSquare, BsDashSquare } from "react-icons/bs";
import ActionBar from "../extra/ActionBar";
import SearchBar from "../extra/SearchBar";
import MyCard from "../extra/Card";
import XTable from "../extra/XTableAnother";
import Form from './Form';
import NoData from "../extra/NoData";

export default function Approval() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const taskallState = useSelector((state) => state?.taskallState);
  const taskState = useSelector((state) => state?.taskState);
  const user = useSelector((state) => state?.loginState?.auth);
  
  const canEditStatus = ["Work Admin", "Work Manager", "Super Admin", "Review", "Reviewer", "Approver"].includes(user?.main_role) || user?.is_superuser;
  
  const [page, setPage] = useState(0);
  const pageSize = 20;
  
  const [view, setView] = useState(1);
  const [open, setOpen] = useState(false);
  const [defV, setDefV] = useState({});

  const fetchTasks = (id = 0) => {
    const url = `work-task/?page=${page + 1}&filter_by={"status__in":["review","approved"]}`;
    dispatch({ type: `${TASKALL}_GET`, payload: url });
  };

  const updateTask = (payload) => {
    if (payload.status === "approved" || payload.status === "rejected") {
      const data = {
        url: "work-task/approve-reject/",
        payload: {
          task_ids: [payload.id],
          action: payload.status === "approved" ? "approve" : "reject",
          remarks: payload.remarks || ""
        }
      };
      dispatch({
        type: `${WORKTASK}_POST`,
        payload: data,
        callback: () => {
          fetchTasks(0);
        }
      });
    } else {
      dispatch({
        type: `${WORKTASK}_PATCH`,
        payload,
        callback: () => {
          fetchTasks(0);
        }
      });
    }
  };

  useEffect(() => {
    fetchTasks(0);
  }, [page]);
  
  useEffect(() => {
    if (taskState?.success) {
      setOpen(!taskState?.success);
      fetchTasks(0);
    }
  }, [taskState?.data]);

  const handleSearch = (key, value) => {
    let query = `work-task/?filter_by={"${key}":"${value}","status__in":["review","approved"]}`;
    if (key === 'status_review') {
        query = `work-task/?filter_by={"status":"review"}`;
    } else if (key === 'status_approved') {
        query = `work-task/?filter_by={"status":"approved"}`;
    }
    dispatch({ type: `${TASKALL}_GET`, payload: query });
  };

  let colorMap = {
    pending: { label: "Pending", color: "#92c3e3ff" },
    in_progress: { label: "In Progress", color: "#a1a1e6ff" },
    approved: { label: "Approved", color: "#179a17ff" },
    rejected: { label: "Rejected", color: "#e96d6dff" },
    completed: { label: "Completed", color: "#0da62eff" },
    blocked: { label: "Blocked", color: "#bb0b0bff" },
    review: { label: "Under Review", color: "#57f6deff" },
  };

  const keyValue = Object.keys(colorMap).map((k) => (
    <option key={k} value={k}>
      {colorMap[k]?.label}
    </option>
  ));

  const handleChecklistClick = (id, label, checklist) => {
    const updatedChecklist = checklist.map((item) => {
      if (item.label === label) {
        return { ...item, status: !item.status };
      }
      return item;
    });

    const selected = updatedChecklist
      .filter((item) => item.status)
      .map((item) => item.label);

    updateTask({
      id: id,
      checkedlist: selected,
    });
  };

  const handleDelete = (id) => {
    dispatch({ type: `${TASK}_DELETE`, payload: id });
  };

  const tableHeadings = ["ID", "Task Name", "Client", "Service", "Status"];
  const tableRows = taskallState?.data?.results?.map((task) => ({
    id: task.id,
    task_name: task.task_name,
    client: task.client_name,
    service: task.service_name,
    status: colorMap[task.status]?.label || task.status,
  }));

  return (
    <>
      {open ? (
        <div>
          <Form
            tasks={(taskallState?.data?.results || [])}
            defaultValues={defV}
            onSubmit={(payload) => {
              // Same transform data logic as view.js
              const selected = Object.keys(payload)
                .filter(key => key?.startsWith("checklist_"))
                .map(key => payload[key]);
                
              let action = payload.id ? `${WORKTASK}_PATCH` : `${WORKTASK}_POST`;
              dispatch({ type: action, payload: { ...payload, checkedlist: selected } });
            }}
            back={() => { setOpen(false) }}
          />
        </div>
      ) : (
        <Container maxWidth="false" className="py-3">
          <Box className="card-bg" sx={{ my: 2, py: 0.2, px: 2, borderRadius: 1 }}>
            <ActionBar
              showGrid={[2, 5]}
              onBtnClick={(type) => {
                if (type === "grid") setView(1);
                if (type === "tabular") setView(2);
              }}
              label={["Task", "Approvals"]}
              hasPermission={false}
            />
          </Box>

          <SearchBar
            bylist={[
              { key: "task__task_name__istartswith", label: "Task Name" },
              { key: "work__work_name__istartswith", label: "Work Name" },
              { key: "work__client__business_name__istartswith", label: "Client Name" },
              { key: "work__service__service_name__istartswith", label: "Service Name" },
              { key: "status_review", label: "Under Review Tasks", hideInput: true },
              { key: "status_approved", label: "Approved Tasks", hideInput: true },
            ]}
            handleSearch={handleSearch}
          />

          {view === 1 && (
            <>
              {!taskallState?.data?.results || taskallState?.data?.results?.length === 0 ? (
                <NoData title="No Tasks Pending Approval" message="There are currently no tasks waiting for review or approval." />
              ) : (
                <>
                  <Grid container spacing={1}>
                    {taskallState?.data?.results?.map((task, idx) => (
                      <Grid key={idx} alignItems="stretch" size={{ xs: 12, sm: 4, md: 3, lg: 3, xl: 3, xxl: 2 }}>
                        <MyCard
                          head={
                            canEditStatus ? (
                            <select
                              onChange={(e) =>
                                updateTask({ id: task.id, status: e.target.value })
                              }
                              defaultValue={task.status}
                              style={{
                                fontSize: 13,
                                background: colorMap[task.status]?.color || "#eee",
                                paddingBottom: 2,
                                paddingTop: 2,
                                paddingLeft: 10,
                                paddingRight: 10,
                                maxWidth: 140,
                                height: "auto",
                                borderRadius: '4px',
                                border: '1px solid #ccc'
                              }}
                            >
                              {keyValue}
                            </select>
                            ) : (
                              <Chip 
                                label={colorMap[task.status]?.label || task.status} 
                                size="small" 
                                sx={{ 
                                  background: colorMap[task.status]?.color || "#eee",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  borderRadius: '4px',
                                  height: 24,
                                  border: '1px solid #ccc'
                                }} 
                              />
                            )
                          }
                          title={task.task_name}
                          subtitle={task.client_creation_date}
                          hideButtons={[0, 0, 0]}
                          onAction={(action) => {
                            if ("more-info" === action) {
                              navigate("/more-info/", {
                                state: {
                                  data: task,
                                  title: "Service Task",
                                  prevNav: { url: "/task-approvals/" },
                                },
                              });
                            } else if ("edit" === action) {
                              const toEdit = { ...task };
                              toEdit.checklist = toEdit.checklist?.map((c, idx) => toEdit[`checklist_${(idx + 1)}`] = c.label);
                              setDefV(toEdit);
                              setOpen(true);
                            } else if ("delete" === action) {
                              handleDelete(task?.id);
                            }
                          }}
                        >
                          <Stack spacing={1.5} sx={{ mt: 1 }}>
                            {task?.continuity && (
                              <Box display="flex" justifyContent="center" alignItems="center" gap={1} sx={{ bgcolor: '#eff6ff', p: 1, borderRadius: 1, border: '1px dashed #bfdbfe' }}>
                                <Typography fontSize={14} color="#60a5fa" title={`Connected to ${task?.continuity}`}>🔗 Continuity</Typography>
                                <Typography color="#3b82f6" fontSize={12} fontWeight={600}>{task?.continuity}</Typography>
                              </Box>
                            )}
                            
                            <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #f1f5f9' }}>
                              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                                <Box>
                                  <Typography fontSize={11} color="textSecondary" textTransform="uppercase" letterSpacing={0.5} fontWeight={600}>Client</Typography>
                                  <Typography fontSize={13} fontWeight={700} color="#1e293b" lineHeight={1.2}>{task.client_name}</Typography>
                                </Box>
                                <Box textAlign="right">
                                  <Typography fontSize={11} color="textSecondary" textTransform="uppercase" letterSpacing={0.5} fontWeight={600}>Service</Typography>
                                  <Typography fontSize={12} fontWeight={700} color="#3b82f6">{task.service_name}</Typography>
                                </Box>
                              </Box>
                              
                              <Typography fontSize={11} color="textSecondary" textTransform="uppercase" letterSpacing={0.5} fontWeight={700} mb={1}>
                                Checklists
                              </Typography>
                              <Stack spacing={0.8}>
                                {(task?.checkedlist || []).map((i, iIdx) => (
                                  <Box
                                    key={iIdx}
                                    onClick={() => handleChecklistClick(task?.id, i.label, task?.checkedlist)}
                                    sx={{ 
                                      display: 'flex', 
                                      justifyContent: 'space-between', 
                                      alignItems: 'center',
                                      p: 1,
                                      borderRadius: 1,
                                      background: i.status ? '#f0fdf4' : '#ffffff',
                                      border: '1px solid',
                                      borderColor: i.status ? '#bbf7d0' : '#e2e8f0',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s',
                                      '&:hover': { background: i.status ? '#dcfce7' : '#f8fafc' }
                                    }}
                                  >
                                    <Typography fontSize={12} fontWeight={500} color={i.status ? '#166534' : '#334155'}>
                                      {i.label}
                                    </Typography>
                                    <Box color={i.status ? '#10b981' : '#ef4444'} display="flex" alignItems="center">
                                      {i.status ? <BsCheckSquare size={14} /> : <BsDashSquare size={14} />}
                                    </Box>
                                  </Box>
                                ))}
                                {(!task?.checkedlist || task.checkedlist.length === 0) && (
                                  <Typography fontSize={11} color="textSecondary" fontStyle="italic">No checklists</Typography>
                                )}
                              </Stack>
                            </Box>
                          </Stack>

                          <Box mt={2}>
                            <Typography fontSize={11} color="textSecondary" sx={{ mb: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Documents</Typography>
                            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                              {["Working File"].map((d) => (
                                <Chip
                                  key={d}
                                  label={d}
                                  size="small"
                                  onClick={() => {
                                    const param = `client=${task.repo_uuid}&object_id=${task.id}&category=${encodeURIComponent(d)}&work=${encodeURIComponent(task.work_name || "")}&service=${encodeURIComponent(task.service_name || "")}&client_name=${encodeURIComponent(task.client_name || "")}&back=/task-approvals/`
                                    navigate(`/repository/documents/0?${param}`)
                                  }}
                                  sx={{ 
                                    fontSize: 11, 
                                    fontWeight: 600,
                                    height: 24,
                                    cursor: 'pointer',
                                    background: '#eff6ff',
                                    color: '#2563eb',
                                    border: '1px solid #bfdbfe',
                                    borderRadius: '6px',
                                    transition: 'all 0.2s',
                                    '&:hover': { background: '#dbeafe', borderColor: '#93c5fd', transform: 'translateY(-1px)' }
                                  }}
                                />
                              ))}
                            </Stack>
                          </Box>

                          {task.description && (
                            <Box mt={1.5}>
                              <Typography color="#64748b" fontSize={12} lineHeight={1.6} sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {task.description}
                              </Typography>
                            </Box>
                          )}
                        </MyCard>
                      </Grid>
                    ))}
                  </Grid>
                  
                  {taskallState?.data?.count > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                      <Pagination
                        count={Math.ceil(taskallState?.data?.count / pageSize)}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        color="primary"
                      />
                    </Box>
                  )}
                </>
              )}
            </>
          )}

          {view === 2 && (
            <XTable
              head={tableHeadings}
              rows={tableRows}
              onEdit={(row) => {
                const originalData = taskallState?.data?.results.find(
                  (task) => task.id === row.id
                );
                if (originalData) {
                  setDefV(originalData);
                  setOpen(true);
                }
              }}
              onDelete={handleDelete}
            />
          )}

        </Container>
      )}
    </>
  );
}
